import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { useLocationMode } from '../data/location.ts'
import { BackLink, Banner } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'
import { StepUpField, useSensitive } from '../ui/sensitive.tsx'

/** Réglages › Localisation (M21, the owner's answer of 2026-10-23: no GPS): whether the app takes the phones' positions at this site. Off unless the club
 *  turns it on. The state is read from the mirror (the site row); the direction (location.manage) changes it with site.set_location_mode@1, an online
 *  command behind a fresh second-factor code (useSensitive: nonce, sendAndWait). Turning it off wipes the positions the site still holds at once; turning
 *  it on asks for the staff notice to be given first (the privacy texts describe the setting as off). The new state comes back with the next pull. */
export function LocationSettingsPage() {
  const { t } = useTranslation()
  const { can, site, syncSoon } = useClub()
  const mode = useLocationMode()
  const s = useSensitive()
  const mayChange = can('location.manage')
  const next = mode === 'on' ? 'off' : 'on'

  async function change() {
    const ok = await s.command('site.set_location_mode@1', { mode: next }, next === 'off' ? 'location.offDone' : 'location.onDone')
    if (ok) syncSoon()
  }

  return (
    <section className="flex flex-col gap-5" data-testid="location-page" data-mode={mode}>
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="map" className="size-7 text-brand dark:text-fairway" />{t('location.title')}</h1>
      <div className="card flex flex-col gap-2">
        <p className="meta">{site.name}</p>
        <p className="text-lg font-bold" data-testid="location-state">{mode === 'on' ? t('location.stateOn') : t('location.stateOff')}</p>
        <p>{mode === 'on' ? t('location.onMeans') : t('location.offMeans')}</p>
      </div>
      {mayChange && (
        <StepUpField s={s}>
          <Banner tone={next === 'on' ? 'warn' : 'info'}><span data-testid="location-before">{next === 'on' ? t('location.beforeOn') : t('location.beforeOff')}</span></Banner>
          <button className={`btn ${next === 'on' ? '' : 'btn-primary'}`} disabled={s.busy || !s.online} onClick={() => void change()} data-testid="location-change">
            {next === 'on' ? t('location.turnOn') : t('location.turnOff')}
          </button>
        </StepUpField>
      )}
      <Link to="/privacy" className="btn" data-testid="location-privacy"><Picto name="clipboard" className="size-5" />{t('location.privacyLink')}</Link>
    </section>
  )
}
